import React from 'react';
import DemoStage from '../DemoStage.jsx';

/**
 * The legal-threshold bar from the case header, sped up: the gradient fill
 * advances scene by scene from teal through amber into red while the
 * "days to legal threshold" counter runs down and the monthly milestone
 * dots light up as they pass. Gradient, dot colours and the day-counter
 * wording mirror CaseLegalTimeline in CaseDetail.jsx (6-month default).
 * Placeholder dates only.
 */

// Same ramp as the real component
const TIMELINE_GRADIENT = 'linear-gradient(to right, #0D9488 0%, #F59E0B 40%, #DC2626 72%, #7F1D1D 100%)';
const DOT_COLORS = ['#0D9488', '#D97706', '#F97316', '#DC2626', '#B91C1C'];

const STEPS = [
  {
    caption: 'The bar runs from the Date of Incident to the legal threshold — 6 months by default.',
    scene: { progress: 8, days: '167d to legal threshold', past: false }, ms: 2600,
  },
  {
    caption: 'A dot marks each month; passed dots light up. On the real bar, hover any dot for its exact date.',
    scene: { progress: 50, days: '91d to legal threshold', past: false }, ms: 2600,
    cursor: 'dot-3',
  },
  {
    caption: 'Amber turning red means the threshold is close — time to chase whatever is still outstanding.',
    scene: { progress: 85, days: '27d to legal threshold', past: false }, ms: 2600,
  },
  {
    caption: 'Past the threshold, the counter flips to “days past legal threshold” and turns dark red.',
    scene: { progress: 100, days: '12d past legal threshold', past: true }, ms: 3200,
    cursor: 'days',
  },
];

const MONTHS = [1, 2, 3, 4, 5];

export default function LegalBarDemo() {
  return (
    <DemoStage steps={STEPS} label="The legal-threshold bar" height={150}>
      {(step) => {
        const scene = step?.scene ?? STEPS[0].scene;
        const { progress, days, past } = scene;
        return (
          <div className="kb-mock" style={{ paddingTop: 6 }}>
            {/* Header row — label + day counter, as on the case header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 16 }}>
              <span style={{
                fontSize: 11, fontWeight: 500, color: 'var(--text-3)',
                textTransform: 'uppercase', letterSpacing: '.06em',
              }}>
                Timeline
              </span>
              <span
                data-kb-target="days"
                style={{
                  fontSize: 11,
                  fontWeight: past ? 600 : 400,
                  color: past ? '#7F1D1D' : 'var(--text-3)',
                  transition: 'color 300ms var(--ease)',
                }}
              >
                {days}
              </span>
            </div>

            {/* Track */}
            <div style={{ position: 'relative', height: 5, background: 'var(--surface-3)', borderRadius: 100 }}>
              {/* Gradient fill, clipped to progress — same technique as the real bar */}
              <div style={{
                position: 'absolute', inset: 0,
                background: TIMELINE_GRADIENT,
                borderRadius: 100,
                clipPath: `inset(0 ${100 - progress}% 0 0 round 100px)`,
                transition: 'clip-path .7s cubic-bezier(0.32,0.72,0,1)',
              }} />

              {/* Start dot */}
              <div style={{
                position: 'absolute', left: 0, top: '50%',
                transform: 'translate(-50%,-50%)',
                width: 9, height: 9, borderRadius: '50%',
                background: '#0D9488',
                border: '2px solid var(--surface)',
                boxShadow: '0 0 0 2px rgba(13,148,136,.2)',
                zIndex: 2,
              }} />

              {/* Monthly milestone dots */}
              {MONTHS.map((m, i) => {
                const pct = (m / 6) * 100;
                const passed = progress >= pct;
                const color = DOT_COLORS[i];
                return (
                  <div
                    key={m}
                    data-kb-target={`dot-${m}`}
                    style={{
                      position: 'absolute', left: `${pct}%`, top: '50%',
                      transform: 'translate(-50%,-50%)',
                      width: 11, height: 11, borderRadius: '50%',
                      background: passed ? color : 'var(--surface)',
                      border: `2px solid ${passed ? color : 'var(--border)'}`,
                      boxShadow: passed ? `0 0 0 3px ${color}28` : 'none',
                      transition: 'all .25s var(--ease)',
                      zIndex: 3,
                    }}
                  />
                );
              })}

              {/* Legal threshold endpoint */}
              <div style={{
                position: 'absolute', right: 0, top: '50%',
                transform: 'translate(50%,-50%)',
                width: 13, height: 13, borderRadius: '50%',
                background: past ? '#7F1D1D' : 'var(--surface)',
                border: '2.5px solid #7F1D1D',
                boxShadow: past ? '0 0 0 3px rgba(127,29,29,.2)' : 'none',
                transition: 'all .25s var(--ease)',
                zIndex: 3,
              }} />
            </div>

            {/* Month labels */}
            <div style={{ position: 'relative', marginTop: 10, height: 14 }}>
              {MONTHS.map((m, i) => {
                const pct = (m / 6) * 100;
                const passed = progress >= pct;
                return (
                  <span key={m} style={{
                    position: 'absolute', left: `${pct}%`,
                    transform: 'translateX(-50%)',
                    fontSize: 10,
                    color: passed ? DOT_COLORS[i] : 'var(--text-3)',
                    fontWeight: passed ? 600 : 400,
                    transition: 'color .25s var(--ease)',
                    userSelect: 'none',
                  }}>
                    {m}m
                  </span>
                );
              })}
              <span style={{
                position: 'absolute', right: 0,
                fontSize: 10,
                color: past ? '#7F1D1D' : 'var(--text-3)',
                fontWeight: past ? 600 : 400,
                userSelect: 'none',
              }}>
                6m · legal
              </span>
            </div>
          </div>
        );
      }}
    </DemoStage>
  );
}
